import { useTranslation } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import { getBudgetChart } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Wallet } from 'lucide-react';

const COLORS = {
  hotel: '#3A86FF', flight: '#00CC99', transport: '#F59E0B',
  food: '#8B5CF6', activity: '#F43F5E', shopping: '#06B6D4', other: '#94A3B8'
};

const CAT_LABELS = {
  hotel: 'Hebergement', flight: 'Vols', transport: 'Transport',
  food: 'Repas', activity: 'Activites', shopping: 'Shopping', other: 'Autre'
};

export const BudgetCharts = ({ tripId, trip }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBudgetChart(tripId).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [tripId]);

  if (loading) return <div className="h-48 bg-white rounded-2xl animate-pulse" />;
  if (!data) return null;

  const pieData = Object.entries(data.by_category)
    .map(([key, val]) => ({ name: CAT_LABELS[key] || key, value: val.actual + val.planned, color: COLORS[key] || '#94A3B8' }))
    .filter(d => d.value > 0);

  const isOver = data.variance > 0;

  return (
    <div className="space-y-4" data-testid="budget-charts">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="widget-card p-4 text-center">
          <p className="text-[11px] text-[#94A3B8] uppercase tracking-wider font-medium">{t('budget.planned')}</p>
          <p className="text-2xl font-bold text-[#1E293B] mt-1">{data.total_planned.toFixed(0)} {data.currency}</p>
        </div>
        <div className="widget-card p-4 text-center">
          <p className="text-[11px] text-[#94A3B8] uppercase tracking-wider font-medium">{t('budget.actual')}</p>
          <p className="text-2xl font-bold text-[#3A86FF] mt-1">{data.total_actual.toFixed(0)} {data.currency}</p>
        </div>
        <div className="widget-card p-4 text-center">
          <p className="text-[11px] text-[#94A3B8] uppercase tracking-wider font-medium">Variance</p>
          <div className="flex items-center justify-center gap-1.5 mt-1">
            {isOver ? <TrendingUp className="w-4 h-4 text-red-500" /> : <TrendingDown className="w-4 h-4 text-emerald-500" />}
            <p className={`text-2xl font-bold ${isOver ? 'text-red-500' : 'text-emerald-500'}`}>
              {data.variance_pct > 0 ? '+' : ''}{data.variance_pct}%
            </p>
          </div>
        </div>
      </div>

      {/* Chart */}
      {data.chart_data.length > 0 && (
        <div className="widget-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#1E293B]">{t('budget.title')} - {t('budget.byCategory')}</h3>
            <Badge className="bg-[#3A86FF]/8 text-[#3A86FF] border-0 text-xs">{data.total_actual.toFixed(0)} {data.currency}</Badge>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.chart_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94A3B8' }} tickFormatter={d => d.split('-').slice(1).join('/')} />
              <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} />
              <Tooltip
                contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0', fontSize: '12px' }}
                formatter={(val, name) => [`${val} ${data.currency}`, CAT_LABELS[name] || name]}
              />
              {Object.keys(COLORS).map(cat => (
                data.chart_data.some(d => d[cat] > 0) && (
                  <Area key={cat} type="monotone" dataKey={cat} stackId="1" stroke={COLORS[cat]} fill={COLORS[cat]} fillOpacity={0.3} />
                )
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Category breakdown */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Pie chart */}
        {pieData.length > 0 && (
          <div className="widget-card p-5">
            <h3 className="text-sm font-bold text-[#1E293B] mb-4">Repartition</h3>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} dataKey="value" paddingAngle={2}>
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(val) => `${val.toFixed(0)} ${data.currency}`} contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Category list */}
        <div className="widget-card p-5">
          <h3 className="text-sm font-bold text-[#1E293B] mb-4">Details par categorie</h3>
          <div className="space-y-2.5">
            {Object.entries(data.by_category).map(([cat, vals]) => (
              <div key={cat} className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[cat] || '#94A3B8' }} />
                <span className="text-sm text-[#1E293B] flex-1">{CAT_LABELS[cat] || cat}</span>
                <span className="text-sm font-semibold text-[#1E293B]">{(vals.actual + vals.planned).toFixed(0)} {data.currency}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
